import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface DashboardProps {
  onNavigate: (tab: string) => void;
  lowStockCount: number;
  newReviewCount: number;
}

interface DashStats {
  // Orders
  totalOrders: number;
  ordersToday: number;
  ordersThisWeek: number;
  ordersThisMonth: number;
  ordersLastMonth: number;
  revenueTotal: number;
  revenueToday: number;
  revenueThisWeek: number;
  revenueThisMonth: number;
  revenueLastMonth: number;
  pendingOrders: number;
  confirmedOrders: number;
  shippedOrders: number;
  deliveredOrders: number;
  cancelledOrders: number;
  bankPendingOrders: number;
  avgOrderValue: number;
  // Customers
  totalCustomers: number;
  newCustomersToday: number;
  newCustomersWeek: number;
  newCustomersMonth: number;
  newCustomersLastMonth: number;
  activeCustomers: number;
  // Products
  totalProducts: number;
  // Reviews
  totalReviews: number;
  avgRating: number;
  // Chart: last 7 days
  last7Days: { label: string; orders: number; revenue: number }[];
  // Top customers
  topCustomers: { name: string; phone: string; orderCount: number; totalSpent: number }[];
  // Recent orders
  recentOrders: { orderRef: string; name: string; finalPrice: number; status: string; date: string }[];
}

function startOfDay(d: Date) { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function startOfWeek(d: Date) { const x = startOfDay(d); x.setDate(x.getDate() - x.getDay()); return x; }
function startOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function startOfLastMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth() - 1, 1); }

const STATUS_MAP: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  pending:   { label: 'قيد المعالجة', color: 'text-amber-700',  bg: 'bg-amber-50 border-amber-200',   dot: 'bg-amber-400' },
  confirmed: { label: 'تم التأكيد',   color: 'text-green-700',  bg: 'bg-green-50 border-green-200',   dot: 'bg-green-400' },
  shipped:   { label: 'تم الشحن',     color: 'text-indigo-700', bg: 'bg-indigo-50 border-indigo-200', dot: 'bg-indigo-400' },
  delivered: { label: 'تم التوصيل',   color: 'text-teal-700',   bg: 'bg-teal-50 border-teal-200',     dot: 'bg-teal-400' },
};

export default function DashboardOverview({ onNavigate, lowStockCount, newReviewCount }: DashboardProps) {
  const [stats, setStats] = useState<DashStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const todayStart     = startOfDay(now);
      const weekStart      = startOfWeek(now);
      const monthStart     = startOfMonth(now);
      const lastMonthStart = startOfLastMonth(now);

      const [ordersRes, customersRes, productsRes, reviewsRes] = await Promise.all([
        supabase.from('orders').select('order_ref, created_at, final_price, status, payment_label, name, phone, customer_id'),
        supabase.from('customers').select('id, phone, name, joined_at'),
        supabase.from('products').select('id'),
        supabase.from('reviews').select('rating'),
      ]);

      const orders = ordersRes.data ?? [];
      const customers = customersRes.data ?? [];
      const products = productsRes.data ?? [];
      const reviews = reviewsRes.data ?? [];

      const isBankTransfer = (o: { payment_label: string }) =>
        o.payment_label === 'التحويل البنكي' || o.payment_label?.includes('بنك') || o.payment_label?.includes('تحويل');

      // Order stats
      const ordersToday     = orders.filter(o => new Date(o.created_at) >= todayStart);
      const ordersThisWeek  = orders.filter(o => new Date(o.created_at) >= weekStart);
      const ordersThisMonth = orders.filter(o => new Date(o.created_at) >= monthStart);
      const ordersLastMonth = orders.filter(o => { const d = new Date(o.created_at); return d >= lastMonthStart && d < monthStart; });

      const sum = (arr: typeof orders) => arr.reduce((s, o) => s + Number(o.final_price ?? 0), 0);

      // Customer stats
      const newCustomersToday     = customers.filter(c => new Date(c.joined_at) >= todayStart).length;
      const newCustomersWeek      = customers.filter(c => new Date(c.joined_at) >= weekStart).length;
      const newCustomersMonth     = customers.filter(c => new Date(c.joined_at) >= monthStart).length;
      const newCustomersLastMonth = customers.filter(c => { const d = new Date(c.joined_at); return d >= lastMonthStart && d < monthStart; }).length;

      // Active customers (have at least 1 order — by phone or customer_id)
      const phonesWithOrders = new Set(orders.map(o => o.phone));
      const customerIdsWithOrders = new Set(orders.map(o => o.customer_id).filter(Boolean));
      const activeCustomers = customers.filter(c =>
        phonesWithOrders.has(c.phone) || customerIdsWithOrders.has(c.id)
      ).length;

      // Last 7 days chart
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(now);
        d.setDate(d.getDate() - (6 - i));
        const dayStart = startOfDay(d);
        const dayEnd = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
        const dayOrders = orders.filter(o => { const od = new Date(o.created_at); return od >= dayStart && od < dayEnd; });
        return {
          label: d.toLocaleDateString('ar-SA', { weekday: 'short' }),
          orders: dayOrders.length,
          revenue: sum(dayOrders),
        };
      });

      // Top customers by spend
      const spendMap: Record<string, { name: string; phone: string; orderCount: number; totalSpent: number }> = {};
      orders.forEach(o => {
        if (!spendMap[o.phone]) spendMap[o.phone] = { name: o.name, phone: o.phone, orderCount: 0, totalSpent: 0 };
        spendMap[o.phone].orderCount += 1;
        spendMap[o.phone].totalSpent += Number(o.final_price ?? 0);
      });
      const topCustomers = Object.values(spendMap)
        .sort((a, b) => b.totalSpent - a.totalSpent)
        .slice(0, 5);

      // Recent orders (last 5)
      const recentOrders = [...orders]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 6)
        .map(o => ({ orderRef: o.order_ref, name: o.name, finalPrice: Number(o.final_price), status: o.status, date: o.created_at }));

      setStats({
        totalOrders: orders.length,
        ordersToday: ordersToday.length,
        ordersThisWeek: ordersThisWeek.length,
        ordersThisMonth: ordersThisMonth.length,
        ordersLastMonth: ordersLastMonth.length,
        revenueTotal: sum(orders),
        revenueToday: sum(ordersToday),
        revenueThisWeek: sum(ordersThisWeek),
        revenueThisMonth: sum(ordersThisMonth),
        revenueLastMonth: sum(ordersLastMonth),
        pendingOrders: orders.filter(o => o.status === 'pending').length,
        deliveredOrders: orders.filter(o => o.status === 'delivered').length,
        bankPendingOrders: orders.filter(o => isBankTransfer(o) && o.status === 'pending').length,
        confirmedOrders: orders.filter(o => o.status === 'confirmed').length,
        shippedOrders: orders.filter(o => o.status === 'shipped').length,
        cancelledOrders: orders.filter(o => o.status === 'cancelled').length,
        avgOrderValue: orders.length ? Math.round(sum(orders) / orders.length) : 0,
        totalCustomers: customers.length,
        newCustomersToday,
        newCustomersWeek,
        newCustomersMonth,
        newCustomersLastMonth,
        activeCustomers,
        totalProducts: products.length,
        totalReviews: reviews.length,
        avgRating: reviews.length ? Math.round((reviews.reduce((s, r) => s + Number(r.rating ?? 0), 0) / reviews.length) * 10) / 10 : 0,
        last7Days,
        topCustomers,
        recentOrders,
      });
      setLastUpdated(new Date());
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <div className="w-12 h-12 border-2 border-gray-200 border-t-[#1a1a1a] rounded-full animate-spin"></div>
        <p className="text-sm text-gray-400">جاري تحميل الإحصائيات...</p>
      </div>
    );
  }

  if (!stats) return null;

  const monthOrderGrowth = stats.ordersLastMonth === 0
    ? (stats.ordersThisMonth > 0 ? 100 : 0)
    : Math.round(((stats.ordersThisMonth - stats.ordersLastMonth) / stats.ordersLastMonth) * 100);

  const monthRevenueGrowth = stats.revenueLastMonth === 0
    ? (stats.revenueThisMonth > 0 ? 100 : 0)
    : Math.round(((stats.revenueThisMonth - stats.revenueLastMonth) / stats.revenueLastMonth) * 100);

  const customerGrowth = stats.newCustomersLastMonth === 0
    ? (stats.newCustomersMonth > 0 ? 100 : 0)
    : Math.round(((stats.newCustomersMonth - stats.newCustomersLastMonth) / stats.newCustomersLastMonth) * 100);

  const conversionRate = stats.totalOrders === 0 ? 0 : Math.round((stats.deliveredOrders / stats.totalOrders) * 100);

  const maxRevenue = Math.max(...stats.last7Days.map(d => d.revenue), 1);
  const maxOrders  = Math.max(...stats.last7Days.map(d => d.orders), 1);

  const GrowthBadge = ({ pct }: { pct: number }) => {
    if (pct === 0) return null;
    return (
      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 ${
        pct > 0 ? 'text-green-600 bg-green-50' : 'text-red-500 bg-red-50'
      }`}>
        <i className={`text-[10px] ${pct > 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}`}></i>
        {Math.abs(pct)}%
      </span>
    );
  };

  return (
    <div className="flex flex-col gap-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-black text-[#1a1a1a]">نظرة عامة</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {new Date().toLocaleDateString('ar-SA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <button
          onClick={fetchStats}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-[#1a1a1a] transition-colors cursor-pointer"
        >
          <i className="ri-refresh-line text-sm"></i>
          {lastUpdated && (
            <span className="hidden sm:inline">
              آخر تحديث {lastUpdated.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </button>
      </div>

      {/* ── Alerts Row ── */}
      {(stats.bankPendingOrders > 0 || lowStockCount > 0 || newReviewCount > 0) && (
        <div className="flex flex-col sm:flex-row gap-2">
          {stats.bankPendingOrders > 0 && (
            <button
              onClick={() => onNavigate('orders')}
              className="flex-1 flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-right cursor-pointer hover:bg-orange-100 transition-colors"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-orange-400 rounded-lg flex-shrink-0 animate-pulse">
                <i className="ri-bank-line text-white text-sm"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-orange-800 text-xs font-black">{stats.bankPendingOrders} تحويل بنكي بانتظار التأكيد</p>
                <p className="text-orange-500 text-[10px]">اضغط للمراجعة</p>
              </div>
              <i className="ri-arrow-left-line text-orange-400 text-sm flex-shrink-0"></i>
            </button>
          )}
          {lowStockCount > 0 && (
            <button
              onClick={() => onNavigate('products')}
              className="flex-1 flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-right cursor-pointer hover:bg-red-100 transition-colors"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-red-400 rounded-lg flex-shrink-0">
                <i className="ri-alarm-warning-line text-white text-sm"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-red-800 text-xs font-black">{lowStockCount} منتج مخزونه منخفض</p>
                <p className="text-red-400 text-[10px]">اضغط لمراجعة المنتجات</p>
              </div>
              <i className="ri-arrow-left-line text-red-400 text-sm flex-shrink-0"></i>
            </button>
          )}
          {newReviewCount > 0 && (
            <button
              onClick={() => onNavigate('reviews')}
              className="flex-1 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-right cursor-pointer hover:bg-amber-100 transition-colors"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-amber-400 rounded-lg flex-shrink-0">
                <i className="ri-star-line text-white text-sm"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-amber-800 text-xs font-black">{newReviewCount} تقييم جديد بانتظار المراجعة</p>
                <p className="text-amber-500 text-[10px]">اضغط لعرض التقييمات</p>
              </div>
              <i className="ri-arrow-left-line text-amber-400 text-sm flex-shrink-0"></i>
            </button>
          )}
        </div>
      )}

      {/* ── Today Snapshot ── */}
      <div className="bg-[#1a1a1a] rounded-2xl p-5">
        <p className="text-white/50 text-xs font-bold mb-4 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block"></span>
          إحصائيات اليوم
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'طلبات اليوم',    value: stats.ordersToday,                                          sub: `${stats.revenueToday.toLocaleString('ar-SA')} ر.س`,  icon: 'ri-shopping-bag-3-line', color: 'text-amber-400' },
            { label: 'عملاء جدد',      value: stats.newCustomersToday,                                    sub: `من ${stats.totalCustomers} إجمالي`,                   icon: 'ri-user-add-line',       color: 'text-green-400' },
            { label: 'إيرادات اليوم',  value: `${stats.revenueToday.toLocaleString('ar-SA')}`,           sub: 'ر.س',                                                  icon: 'ri-money-dollar-circle-line', color: 'text-teal-400' },
            { label: 'طلبات معلقة',    value: stats.pendingOrders,                                        sub: 'تحتاج متابعة',                                         icon: 'ri-time-line',           color: 'text-red-400' },
          ].map(s => (
            <div key={s.label} className="flex flex-col gap-1">
              <div className="flex items-center gap-2 mb-1">
                <i className={`${s.icon} ${s.color} text-sm`}></i>
                <span className="text-white/40 text-[10px]">{s.label}</span>
              </div>
              <p className="text-white text-2xl font-black">{s.value}</p>
              <p className="text-white/30 text-[10px]">{s.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* Orders This Month */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg">
              <i className="ri-file-list-3-line text-amber-600 text-sm"></i>
            </div>
            <GrowthBadge pct={monthOrderGrowth} />
          </div>
          <p className="text-2xl font-black text-[#1a1a1a]">{stats.ordersThisMonth}</p>
          <p className="text-xs text-gray-400">طلب هذا الشهر</p>
          <p className="text-[10px] text-gray-400">الأسبوع: <span className="font-bold text-gray-600">{stats.ordersThisWeek}</span></p>
        </div>

        {/* Revenue This Month */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-lg">
              <i className="ri-money-dollar-circle-line text-green-600 text-sm"></i>
            </div>
            <GrowthBadge pct={monthRevenueGrowth} />
          </div>
          <p className="text-xl font-black text-[#1a1a1a] leading-tight">{stats.revenueThisMonth.toLocaleString('ar-SA')}</p>
          <p className="text-xs text-gray-400">ر.س إيرادات الشهر</p>
          <p className="text-[10px] text-gray-400">الإجمالي: <span className="font-bold text-gray-600">{stats.revenueTotal.toLocaleString('ar-SA')}</span></p>
        </div>

        {/* New Customers */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg">
              <i className="ri-group-line text-teal-600 text-sm"></i>
            </div>
            <GrowthBadge pct={customerGrowth} />
          </div>
          <p className="text-2xl font-black text-[#1a1a1a]">{stats.newCustomersMonth}</p>
          <p className="text-xs text-gray-400">عميل جديد هذا الشهر</p>
          <p className="text-[10px] text-gray-400">الأسبوع: <span className="font-bold text-gray-600">{stats.newCustomersWeek}</span></p>
        </div>

        {/* Conversion Rate */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="w-8 h-8 flex items-center justify-center bg-indigo-50 rounded-lg">
              <i className="ri-percent-line text-indigo-600 text-sm"></i>
            </div>
          </div>
          <p className="text-2xl font-black text-[#1a1a1a]">{conversionRate}%</p>
          <p className="text-xs text-gray-400">نسبة التحويل</p>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1">
            <div className="h-full bg-indigo-400 rounded-full" style={{ width: `${conversionRate}%` }}></div>
          </div>
        </div>
      </div>

      {/* ── Chart + Status Distribution ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* 7-day chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-sm font-bold text-[#1a1a1a]">الطلبات والإيرادات</p>
              <p className="text-xs text-gray-400 mt-0.5">آخر 7 أيام</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-sm bg-[#1a1a1a]"></div>
                <span className="text-[10px] text-gray-400">طلبات</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-sm bg-amber-300"></div>
                <span className="text-[10px] text-gray-400">إيرادات</span>
              </div>
            </div>
          </div>

          <div className="flex items-end gap-2 h-28">
            {stats.last7Days.map((day, i) => {
              const orderH  = (day.orders  / maxOrders)  * 100;
              const revenueH = (day.revenue / maxRevenue) * 100;
              const isToday = i === 6;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative" style={{ height: '100%' }}>
                  <div className="flex-1 flex items-end gap-0.5 w-full">
                    <div
                      className={`flex-1 rounded-t-md transition-all ${isToday ? 'bg-amber-400' : 'bg-amber-200 group-hover:bg-amber-300'}`}
                      style={{ height: `${Math.max(revenueH, day.revenue > 0 ? 8 : 2)}%` }}
                    ></div>
                    <div
                      className={`flex-1 rounded-t-md transition-all ${isToday ? 'bg-[#1a1a1a]' : 'bg-gray-300 group-hover:bg-gray-400'}`}
                      style={{ height: `${Math.max(orderH, day.orders > 0 ? 8 : 2)}%` }}
                    ></div>
                  </div>
                  {/* Tooltip */}
                  {(day.orders > 0 || day.revenue > 0) && (
                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-[#1a1a1a] text-white text-[9px] px-2 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-center">
                      <p className="font-bold">{day.orders} طلب</p>
                      {day.revenue > 0 && <p className="text-gray-300">{day.revenue.toLocaleString('ar-SA')} ر.س</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* X labels */}
          <div className="flex gap-2 mt-2">
            {stats.last7Days.map((day, i) => (
              <div key={i} className="flex-1 text-center">
                <p className={`text-[10px] ${i === 6 ? 'text-[#1a1a1a] font-black' : 'text-gray-400'}`}>{day.label}</p>
                <p className={`text-[10px] font-bold ${i === 6 ? 'text-[#1a1a1a]' : 'text-gray-400'}`}>{day.orders}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Status distribution */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 flex flex-col gap-4">
          <div>
            <p className="text-sm font-bold text-[#1a1a1a]">توزيع الطلبات</p>
            <p className="text-xs text-gray-400 mt-0.5">حسب الحالة</p>
          </div>

          <div className="flex flex-col gap-3">
            {[
              { key: 'pending',   label: 'قيد المعالجة', count: stats.pendingOrders,   color: 'bg-amber-400' },
              { key: 'confirmed', label: 'تم التأكيد',   count: stats.confirmedOrders, color: 'bg-green-400' },
              { key: 'shipped',   label: 'تم الشحن',     count: stats.shippedOrders,   color: 'bg-indigo-400' },
              { key: 'delivered', label: 'تم التوصيل',   count: stats.deliveredOrders, color: 'bg-teal-400' },
              { key: 'cancelled', label: 'ملغي',          count: stats.cancelledOrders, color: 'bg-red-300' },
            ].filter(s => s.count > 0).map(s => {
              const pct = stats.totalOrders === 0 ? 0 : Math.round((s.count / stats.totalOrders) * 100);
              return (
                <div key={s.key}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${s.color}`}></div>
                      <span className="text-xs text-gray-600">{s.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-[#1a1a1a]">{s.count}</span>
                      <span className="text-[10px] text-gray-400 w-7 text-left">{pct}%</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${s.color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }}></div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="border-t border-gray-100 pt-3 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">متوسط قيمة الطلب</span>
              <span className="text-sm font-black text-[#1a1a1a]">{stats.avgOrderValue.toLocaleString('ar-SA')} ر.س</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">متوسط التقييم</span>
              <span className="text-sm font-black text-amber-500 flex items-center gap-1">
                <i className="ri-star-fill text-xs"></i>
                {stats.avgRating || '—'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">إجمالي التقييمات</span>
              <span className="text-sm font-black text-[#1a1a1a]">{stats.totalReviews}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom Row: Recent Orders + Top Customers ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Recent Orders */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <p className="text-sm font-bold text-[#1a1a1a]">آخر الطلبات</p>
            <button
              onClick={() => onNavigate('orders')}
              className="text-xs text-gray-400 hover:text-[#1a1a1a] transition-colors cursor-pointer flex items-center gap-1"
            >
              عرض الكل
              <i className="ri-arrow-left-line text-xs"></i>
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {stats.recentOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <i className="ri-inbox-line text-2xl text-gray-200"></i>
                <p className="text-xs text-gray-400">لا توجد طلبات بعد</p>
              </div>
            ) : stats.recentOrders.map(o => {
              const st = STATUS_MAP[o.status] ?? STATUS_MAP['pending'];
              return (
                <div key={o.orderRef} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${st.dot}`}></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-bold text-[#1a1a1a]">#{o.orderRef}</p>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${st.bg} ${st.color}`}>{st.label}</span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5 truncate">{o.name} · {new Date(o.date).toLocaleDateString('ar-SA', { month: 'short', day: 'numeric' })}</p>
                  </div>
                  <p className="text-xs font-black text-[#1a1a1a] flex-shrink-0">{o.finalPrice.toLocaleString('ar-SA')} ر.س</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Customers */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <p className="text-sm font-bold text-[#1a1a1a]">أفضل العملاء</p>
            <button
              onClick={() => onNavigate('customers')}
              className="text-xs text-gray-400 hover:text-[#1a1a1a] transition-colors cursor-pointer flex items-center gap-1"
            >
              عرض الكل
              <i className="ri-arrow-left-line text-xs"></i>
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {stats.topCustomers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <i className="ri-group-line text-2xl text-gray-200"></i>
                <p className="text-xs text-gray-400">لا يوجد عملاء بعد</p>
              </div>
            ) : stats.topCustomers.map((c, i) => (
              <div key={c.phone} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors">
                <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 text-xs font-black ${
                  i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-gray-300 text-gray-700' : i === 2 ? 'bg-orange-300 text-white' : 'bg-gray-100 text-gray-500'
                }`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-[#1a1a1a] truncate">{c.name}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{c.orderCount} طلب</p>
                </div>
                <p className="text-xs font-black text-[#1a1a1a] flex-shrink-0">{c.totalSpent.toLocaleString('ar-SA')} ر.س</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Quick Actions ── */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <p className="text-sm font-bold text-[#1a1a1a] mb-4">وصول سريع</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'إضافة منتج',    icon: 'ri-add-box-line',        tab: 'products',  color: 'bg-[#1a1a1a] text-white' },
            { label: 'إدارة الطلبات', icon: 'ri-file-list-3-line',    tab: 'orders',    color: 'bg-amber-50 text-amber-700 border border-amber-200' },
            { label: 'العملاء',       icon: 'ri-group-line',           tab: 'customers', color: 'bg-green-50 text-green-700 border border-green-200' },
            { label: 'إعدادات واتساب',icon: 'ri-whatsapp-line',        tab: 'whatsapp',  color: 'bg-green-500 text-white' },
          ].map(a => (
            <button
              key={a.tab}
              onClick={() => onNavigate(a.tab)}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-bold transition-all cursor-pointer hover:scale-[1.02] whitespace-nowrap ${a.color}`}
            >
              <i className={`${a.icon} text-base`}></i>
              {a.label}
            </button>
          ))}
        </div>
      </div>

    </div>
  );
}
